"use client";

import type { TemplateSlot, TemplateType } from "@/features/calendar/types/templates";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Icon } from "@iconify-icon/react";
import { AnimatePresence, motion } from "motion/react";

import { Dialog, DialogContent } from "../dialog.tsx";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { TimePicker } from "@/features/calendar/components/create-slot/time-picker";
import { Card, CardContent } from "@/components/ui/card";
import { LessonHost } from "@/features/calendar/types/create-slot.ts";
import { useHostingOptions } from "@/features/calendar/types/hostingOptions.tsx";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Animation variants
const slideUpAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 20 },
};

const staggeredContainerAnimation = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
  exit: { opacity: 0 },
};

interface TemplateSlotEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateType: TemplateType;
  slotData: TemplateSlot | null;
  onSuccess?: () => void;
}

export function TemplateSlotEditDialog({
  isOpen,
  onClose,
  templateId,
  templateType,
  slotData,
  onSuccess,
}: TemplateSlotEditDialogProps) {
  const { t } = useTranslation(["common", "calendar", "forms"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hostingOptions = useHostingOptions();

  // Create the schema with the translation function
  const editTemplateSlotSchema = useMemo(() => {
    return z
      .object({
        start_time: z
          .string()
          .regex(/^\d{2}:\d{2}$/, { message: t("forms:templates.validation.startTimeFormat") }),
        end_time: z
          .string()
          .regex(/^\d{2}:\d{2}$/, { message: t("forms:templates.validation.endTimeFormat") }),
        day_of_week: z.number().min(1).max(7).optional(),
        lesson_host: z.string(),
        lesson_type: z.string().default("individual"),
        communication_details: z.string().optional(),
        invitation_message: z.string().optional(),
        topic: z.string().optional(),
      })
      .refine(
        (data) => {
          // Convert times to minutes for comparison
          const [startHour, startMinute] = data.start_time.split(":").map(Number);
          const [endHour, endMinute] = data.end_time.split(":").map(Number);

          const startMinutes = startHour * 60 + startMinute;
          const endMinutes = endHour * 60 + endMinute;

          // Check if end time is after start time
          return endMinutes > startMinutes;
        },
        {
          message: t("forms:templates.validation.endTimeAfterStart"),
          path: ["end_time"],
        },
      )
      .refine(
        (data) => {
          // Convert times to minutes for comparison
          const [startHour, startMinute] = data.start_time.split(":").map(Number);
          const [endHour, endMinute] = data.end_time.split(":").map(Number);

          const startMinutes = startHour * 60 + startMinute;
          const endMinutes = endHour * 60 + endMinute;

          // Check if duration is at least 10 minutes
          return endMinutes - startMinutes >= 10;
        },
        {
          message: t("forms:templates.validation.durationMinimum"),
          path: ["end_time"],
        },
      )
      .refine(
        (data) => {
          // Convert times to minutes for comparison
          const [startHour, startMinute] = data.start_time.split(":").map(Number);
          const [endHour, endMinute] = data.end_time.split(":").map(Number);

          const startMinutes = startHour * 60 + startMinute;
          const endMinutes = endHour * 60 + endMinute;

          // Check if duration is at most 6 hours (360 minutes)
          return endMinutes - startMinutes <= 360;
        },
        {
          message: t("forms:templates.validation.durationMaximum"),
          path: ["end_time"],
        },
      )
      .refine(
        (data) => {
          // Communication details are required if lesson_host is 'other_way'
          return (
            data.lesson_host !== "other_way" ||
            (data.communication_details && data.communication_details.trim() !== "")
          );
        },
        {
          message: t("forms:templates.validation.communicationDetailsRequired"),
          path: ["communication_details"],
        },
      );
  }, [t]);

  const form = useForm({
    resolver: zodResolver(editTemplateSlotSchema),
    mode: "onSubmit",
    defaultValues: {
      start_time: "",
      end_time: "",
      day_of_week: templateType === "weekly" ? 1 : undefined,
      lesson_host: LessonHost.Ximly,
      lesson_type: "individual",
      communication_details: "",
      invitation_message: "",
      topic: "",
    },
  });

  // Reset and populate form when dialog opens with slot data
  useEffect(() => {
    if (isOpen && slotData) {
      // Ensure time is properly formatted as HH:MM
      const formattedStartTime = slotData.start_time.slice(0, 5);
      const formattedEndTime = slotData.end_time.slice(0, 5);

      form.reset({
        start_time: formattedStartTime,
        end_time: formattedEndTime,
        day_of_week: slotData.day_of_week || undefined,
        lesson_host: slotData.lesson_host as LessonHost,
        lesson_type: slotData.lesson_type || "individual",
        communication_details: slotData.communication_details || "",
      });
    }
  }, [isOpen, slotData, form]);

  async function onSubmit(data: any) {
    if (!slotData?.id) return;

    // Manually validate the form to ensure refine methods are triggered
    const isValid = await form.trigger();

    if (!isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Create a copy of the data to modify
      const requestData = { ...data };

      // For daily templates, remove day_of_week from the request
      if (templateType === "daily" && "day_of_week" in requestData) {
        delete requestData.day_of_week;
      }

      // Use PUT request for updating
      await axiosApiInstance.put(
        `${API_ENDPOINTS.TUTOR.TEMPLATES.LIST}/${templateId}/slots/${slotData.id}`,
        requestData,
      );

      toast.success(t("calendar:templates.slotUpdated"), {
        description: t("calendar:templates.slotUpdatedDescription"),
      });

      setIsSubmitting(false);
      onClose();
      onSuccess?.();
    } catch (error: any) {
      console.error("Failed to update slot:", error);

      // Handle validation errors from the API
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;

        Object.entries(apiErrors).forEach(([field, messages]) => {
          if (field.includes(".")) {
            // Handle nested fields like price.amount
            const [parent, child] = field.split(".");

            form.setError(`${parent}.${child}` as any, {
              type: "server",
              message: Array.isArray(messages) ? messages[0] : (messages as string),
            });
          } else {
            // Handle regular fields
            form.setError(field as any, {
              type: "server",
              message: Array.isArray(messages) ? messages[0] : (messages as string),
            });
          }
        });
      } else if (error.response?.data?.code === "database_overlap") {
        toast.error(t("common:error"), {
          description: t("calendar:templates.slotOverlapError"),
        });
      } else {
        toast.error(t("common:error"), {
          description: t("common:errors.genericErrorTitle"),
        });
      }

      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        aria-describedby={undefined}
        className="bg-background absolute! left-0 flex h-dvh max-w-full flex-col gap-0 rounded-e-none! border-none p-0 focus-visible:ring-0 focus-visible:ring-offset-0 md:left-[35%] md:max-w-[65%] xl:left-[60%] xl:max-w-[40vw]"
      >
        <motion.div
          animate={{ x: 0, opacity: 1 }}
          className="flex h-full flex-col"
          exit={{ x: 100, opacity: 0 }}
          initial={{ x: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Form {...form}>
            <ScrollArea className="h-[calc(100dvh_-_130px)]">
              <AnimatePresence mode="popLayout">
                <motion.div
                  key="creation-form"
                  animate="animate"
                  className="space-y-6 p-6"
                  exit="exit"
                  initial="initial"
                  variants={staggeredContainerAnimation}
                >
                  {/* Time and Host Sections */}
                  <motion.div variants={slideUpAnimation}>
                    <Card className="bg-foreground/50 relative z-50 rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                      <div className="absolute inset-0 overflow-hidden opacity-5">
                        <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
                        <div className="absolute -bottom-12 -left-12 h-24 w-24 rounded-full bg-gradient-to-tr from-green-400 to-emerald-500" />
                      </div>
                      <CardContent className="relative z-50 p-6">
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="start_time"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-muted-foreground mb-3 block text-base font-semibold">
                                  {t("calendar:templates.startTime")}
                                </FormLabel>
                                <FormControl>
                                  <div className="h-12">
                                    <TimePicker
                                      className="bg-muted/20 rounded-xl"
                                      value={field.value}
                                      onChange={field.onChange}
                                    >
                                      <div className="bg-muted/20 hover:bg-muted/30 flex h-12 cursor-pointer items-center gap-3 rounded-xl border-none px-4 backdrop-blur-sm transition-colors">
                                        <Icon
                                          className="text-muted-foreground"
                                          height={"1.25rem"}
                                          icon="tabler:clock"
                                          width={"1.25rem"}
                                        />
                                        <span className="text-accent-foreground font-medium">
                                          {field.value}
                                        </span>
                                      </div>
                                    </TimePicker>
                                  </div>
                                </FormControl>
                                <FormMessage className="mt-2" />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="end_time"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel className="text-muted-foreground mb-3 block text-base font-semibold">
                                  {t("calendar:templates.endTime")}
                                </FormLabel>
                                <FormControl>
                                  <div className="h-12">
                                    <TimePicker
                                      className="bg-muted/20 rounded-xl"
                                      value={field.value}
                                      onChange={field.onChange}
                                    >
                                      <div className="bg-muted/20 hover:bg-muted/30 flex h-12 cursor-pointer items-center gap-3 rounded-xl border-none px-4 backdrop-blur-sm transition-colors">
                                        <Icon
                                          className="text-muted-foreground"
                                          height={"1.25rem"}
                                          icon="tabler:clock"
                                          width={"1.25rem"}
                                        />
                                        <span className="text-accent-foreground font-medium">
                                          {field.value}
                                        </span>
                                      </div>
                                    </TimePicker>
                                  </div>
                                </FormControl>
                                <FormMessage className="mt-2" />
                              </FormItem>
                            )}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div variants={slideUpAnimation}>
                    <Card className="bg-foreground/50 relative rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                      <div className="absolute inset-0 overflow-hidden opacity-5">
                        <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br from-violet-400 to-purple-600" />
                        <div className="absolute -right-16 -bottom-16 h-32 w-32 rounded-full bg-gradient-to-tr from-orange-400 to-red-500" />
                      </div>

                      <CardContent className="relative z-50 p-6">
                        <FormField
                          control={form.control}
                          name="lesson_host"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-muted-foreground mb-4 block text-base font-semibold">
                                {t("calendar:templates.lessonHost")}
                              </FormLabel>
                              <RadioGroup
                                className="flex flex-col space-y-4"
                                value={field.value}
                                onValueChange={field.onChange}
                              >
                                {hostingOptions.map((option, index) => (
                                  <motion.div
                                    key={`host-option-${option.id}`}
                                    animate={{ opacity: 1 }}
                                    className={`relative rounded-xl p-4 backdrop-blur-sm transition-all ${
                                      field.value === option.id
                                        ? "bg-muted/30 shadow-lg"
                                        : option.disabled
                                          ? "bg-muted/20 cursor-not-allowed opacity-70"
                                          : "bg-muted/20 hover:bg-muted/25 cursor-pointer"
                                    }`}
                                    initial={{ opacity: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    onClick={() => {
                                      if (option.disabled) {
                                        return;
                                      }

                                      field.onChange(option.id);
                                    }}
                                  >
                                    <FormItem className="m-0 flex items-center space-y-0 space-x-4">
                                      <FormControl>
                                        <RadioGroupItem
                                          className="text-muted-foreground border-muted-foreground data-[state=checked]:border-accent-foreground data-[state=checked]:bg-accent-foreground data-[state=checked]:text-accent-foreground"
                                          disabled={option.disabled}
                                          value={option.id}
                                        />
                                      </FormControl>
                                      <FormLabel className="relative m-0 flex w-full cursor-pointer items-center justify-between text-start font-normal">
                                        <div className="flex items-center gap-4">
                                          <div
                                            className={`flex size-10 items-center justify-center rounded-xl ${option.disabled ? "opacity-70" : ""}`}
                                          >
                                            {option.icon}
                                          </div>
                                          <div>
                                            <div className="flex items-center gap-2">
                                              <span
                                                className={`${option.disabled ? "text-muted-foreground" : "text-accent-foreground"} text-lg font-semibold`}
                                              >
                                                {option.title}
                                              </span>
                                              {option.id === LessonHost.Ximly && (
                                                <Badge className="bg-accent-foreground text-primary absolute top-2 right-2 rounded-full">
                                                  {t("calendar:templates.recommended")}
                                                </Badge>
                                              )}
                                            </div>
                                            <p className="text-muted-foreground mt-1 text-sm">
                                              {option.description}
                                            </p>
                                            {option.hint && option.disabled && (
                                              <TooltipProvider>
                                                <Tooltip delayDuration={200}>
                                                  <TooltipTrigger asChild>
                                                    <div className="bg-muted/30 mt-3 cursor-help rounded-md p-3">
                                                      <p className="text-accent-foreground text-sm font-medium">
                                                        {option.hint.title}
                                                      </p>
                                                      <p className="text-muted-foreground mt-1 text-xs">
                                                        {option.hint.description}
                                                      </p>
                                                      <span className="mt-2 inline-flex items-center gap-1 text-xs text-blue-400 underline">
                                                        {option.hint.actionLabel}
                                                      </span>
                                                    </div>
                                                  </TooltipTrigger>
                                                  <TooltipContent className="max-w-xs text-left">
                                                    <p className="text-accent-foreground text-sm font-medium">
                                                      {option.hint.title}
                                                    </p>
                                                    <p className="text-muted-foreground mt-1 text-xs">
                                                      {option.hint.description}
                                                    </p>
                                                    <a
                                                      className="mt-2 inline-flex items-center gap-1 text-xs text-blue-400 underline"
                                                      href={option.hint.actionHref}
                                                      rel="noreferrer"
                                                      target="_blank"
                                                    >
                                                      <span>{option.hint.actionLabel}</span>
                                                      <Icon
                                                        height={"1rem"}
                                                        icon="tabler:arrow-up-right"
                                                        width={"1rem"}
                                                      />
                                                    </a>
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                            )}
                                            {option.id === LessonHost.Ximly && (
                                              <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t("calendar:templates.additionalFee", {
                                                  fee: "0.36$/hour",
                                                })}
                                              </p>
                                            )}
                                            {option.id === LessonHost.Ximly && (
                                              <p className="text-muted-foreground mt-1 text-xs italic">
                                                {t("calendar:templates.additionalFee", {
                                                  fee: "0.36$/hour",
                                                })}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </FormLabel>
                                    </FormItem>
                                    {option.disabled && (
                                      <div className="bg-background/60 pointer-events-none absolute inset-0 rounded-xl" />
                                    )}
                                  </motion.div>
                                ))}
                              </RadioGroup>
                              <FormMessage className="mt-2" />
                            </FormItem>
                          )}
                        />

                        <AnimatePresence>
                          {form.watch("lesson_host") === LessonHost.OtherWay && (
                            <motion.div
                              key="communication-details"
                              animate={{ opacity: 1, height: "auto" }}
                              className="mt-6"
                              exit={{ opacity: 0, height: 0 }}
                              initial={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <FormField
                                control={form.control}
                                name="communication_details"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel className="text-muted-foreground mb-3 block text-base font-semibold">
                                      {t("forms:hostMethodSelector.communicationDetails")}
                                    </FormLabel>
                                    <FormControl>
                                      <Textarea
                                        className="bg-muted/20 hover:bg-muted/30 min-h-[100px] resize-none rounded-xl border-none backdrop-blur-sm transition-colors"
                                        placeholder={t(
                                          "forms:hostMethodSelector.communicationDetailsPlaceholder",
                                        )}
                                        {...field}
                                        maxLength={255}
                                      />
                                    </FormControl>
                                    <FormMessage className="mt-2" />
                                  </FormItem>
                                )}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </ScrollArea>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="border-border bg-background flex h-16 items-center justify-end border-t px-6 py-1"
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.3 }}
            >
              <Button
                disabled={isSubmitting}
                variant={"foreground"}
                onClick={form.handleSubmit(onSubmit)}
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {t("common:updating")}
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <Icon height="18" icon="tabler:check" width="18" />
                    <span>{t("common:save")}</span>
                  </div>
                )}
              </Button>
            </motion.div>
          </Form>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
